// Zone-profile API — served by the zoneProfileApi lambda behind the same API the
// requirements register / re-extract use (REACT_APP_API_BASE_REQUIREMENTS).
const BASE = process.env.REACT_APP_API_BASE_REQUIREMENTS || 'http://localhost:8000';

function authHeaders() {
  const idToken = localStorage.getItem('id_token');
  return idToken
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }
    : { 'Content-Type': 'application/json' };
}

function base(projectId, requirementId, linkId) {
  return `${BASE}/zone-profile/${encodeURIComponent(projectId)}/${encodeURIComponent(requirementId)}/${encodeURIComponent(linkId)}`;
}

// Zone profiles for a project (server-side, authoritative): { pending, active }.
// `pending` → awaiting confirmation (drives the resume banner); `active` →
// confirmed (drives the re-check / refilter control). Throws on failure so
// callers can fall back to the client cache.
export async function getProjectZoneProfiles(projectId) {
  const res = await fetch(`${BASE}/zone-profile/${encodeURIComponent(projectId)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load zone profiles');
  const data = await res.json();
  return { pending: data.pending || [], active: data.active || [] };
}

// ── Client-side stopgap cache ────────────────────────────────────────────────
// Until the /pending route is wired (and as a resilience net), we remember any
// pending zone-confirm payload we've seen via the notification, keyed by project,
// so the Requirements Register can still offer a resume even if the notification
// is dismissed/lost. Best-effort, per-browser.
const _CACHE_KEY = 'codiii.pendingZoneProfiles';

function _readCache() {
  try { return JSON.parse(localStorage.getItem(_CACHE_KEY) || '{}') || {}; }
  catch { return {}; }
}

export function cachePendingZoneProfile(payload) {
  if (!payload || !payload.projectId) return;
  const map = _readCache();
  map[payload.projectId] = {
    projectId:     payload.projectId,
    requirementId: payload.requirementId,
    linkId:        payload.linkId,
    docName:       payload.docName || '',
  };
  try { localStorage.setItem(_CACHE_KEY, JSON.stringify(map)); } catch { /* quota — ignore */ }
}

export function getCachedPendingZoneProfile(projectId) {
  const entry = _readCache()[projectId];
  return entry && entry.requirementId && entry.linkId ? entry : null;
}

export function clearCachedPendingZoneProfile(projectId) {
  const map = _readCache();
  if (map[projectId]) {
    delete map[projectId];
    try { localStorage.setItem(_CACHE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
  }
}

export async function getZoneProfile(projectId, requirementId, linkId) {
  const res = await fetch(base(projectId, requirementId, linkId), { headers: authHeaders() });
  if (!res.ok) {
    const err = new Error('Failed to load zone profile');
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return data.zoneProfile;
}

// Confirm the decoded profile (with optional edits) and RESUME extraction.
export async function confirmZoneProfile(projectId, requirementId, linkId, edits, userId) {
  const res = await fetch(`${base(projectId, requirementId, linkId)}/confirm`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ edits: edits || {}, userId: userId || '' }),
  });
  if (!res.ok) throw new Error('Failed to confirm zone profile');
  return res.json();
}

// Re-edit a profile AFTER extraction already ran → recompute relevance (no re-extraction).
export async function refilterZoneProfile(projectId, requirementId, linkId, edits) {
  const res = await fetch(`${base(projectId, requirementId, linkId)}/refilter`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ edits: edits || {} }),
  });
  if (!res.ok) throw new Error('Failed to re-filter requirements');
  return res.json();
}
